import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, TrendingUp, Target, Zap, Shield } from 'lucide-react';

const InvestorsPage = () => {
  useEffect(() => {
    document.title = 'For Investors | Nexus LBE';
  }, []);

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden pt-16 pb-10 lg:pt-20 lg:pb-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <Badge variant="outline">For Investors</Badge>
              <h1 className="font-['Geist'] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
                Our unique "LBE-as-a-Service" model transforms location-based entertainment into a scalable platform
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Standardised infrastructure with a pipeline of titles, enabling experiences to rotate every 6–12 months without a full structural rebuild. This creates continuous interest, lower capex and aids with faster multi-city expansion.
              </p>
            </div>
            <div className="relative rounded-xl overflow-hidden aspect-[4/3] bg-secondary shadow-2xl">
              <img 
                src="/images/investor-partner.jpg" 
                alt="Investor partners - predictable unit economics with measurable outcomes"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center mb-8">
            <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
              Why invest in NEXUS LBE
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-6 space-y-3">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-['Geist'] text-xl">Formatted, Scalable Model</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  80% standardized platform enables rapid expansion with proven operational excellence. 
                  Unlike one-off LBEs, our format scales knowledge and reduces risk.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 space-y-3">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-['Geist'] text-xl">De-risked Through Format</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Proven 5-phase psychology, rotating titles reduce hit-risk, diversified revenue streams 
                  (tickets, merch, F&B), and shorter title run times enable quick pivots.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 space-y-3">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-['Geist'] text-xl">Strong IP Pipeline</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Partnerships with major IP owners ensure continuous flow of fresh, relevant titles. 
                  Leverages marketing cycles for built-in awareness and demand.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 space-y-3">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Zap className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-['Geist'] text-xl">First-Mover Advantage</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Establishing category leadership in Europe and GCC before competitors enter with formatted 
                  approaches. Capturing prime real estate partnerships early.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Unit Economics */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center mb-8">
            <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
              Unit economics overview
            </h2>
            <p className="text-muted-foreground">Formatted approach delivers predictable, measurable outcomes.</p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardContent className="p-8 md:p-12 space-y-8">
                <div>
                  <h3 className="font-['Geist'] text-xl mb-4">Revenue Streams</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <div className="text-2xl font-['Geist'] font-bold text-primary mb-1">~70%</div>
                      <p className="text-sm text-muted-foreground">Ticketing</p>
                    </div>
                    <div>
                      <div className="text-2xl font-['Geist'] font-bold text-primary mb-1">~20%</div>
                      <p className="text-sm text-muted-foreground">Merchandise</p>
                    </div>
                    <div>
                      <div className="text-2xl font-['Geist'] font-bold text-primary mb-1">~10%</div>
                      <p className="text-sm text-muted-foreground">F&B</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-['Geist'] text-xl mb-4">Growth Path</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span><strong>Phase 0:</strong> e.g. Berlin pilot to validate format and operations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span><strong>Phase 1:</strong> Flagship rollout in e.g. London, e.g. Hamburg, e.g. Riyadh</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span><strong>Phase 2:</strong> Multi-city expansion + franchise model introduction</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span><strong>5-year target:</strong> 15+ sites, building a digital experience LBE@Home</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Market Opportunity */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center mb-8">
            <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
              The LBE market opportunity
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Global LBE market growing at 22.9% CAGR, driven by consumer demand for real-world experiences.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6 text-center space-y-2">
                <div className="text-4xl font-['Geist'] font-bold text-primary">$23.3B</div>
                <p className="text-sm font-semibold">Projected 2032 Market</p>
                <p className="text-xs text-muted-foreground">Up from $6.18B in 2025</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center space-y-2">
                <div className="text-4xl font-['Geist'] font-bold text-primary">22.9%</div>
                <p className="text-sm font-semibold">CAGR 2024-2029</p>
                <p className="text-xs text-muted-foreground">High-growth entertainment sector</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center space-y-2">
                <div className="text-4xl font-['Geist'] font-bold text-primary">Europe + GCC</div>
                <p className="text-sm font-semibold">Target Regions</p>
                <p className="text-xs text-muted-foreground">Urban density + tech adoption</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl text-center">
          <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
            Join us in building the category
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-muted-foreground">
            We're raising seed funding to launch our pilot and Phase 1 flagships. Let's discuss the opportunity.
          </p>
          <Link to="/contact">
            <Button size="lg" className="h-12 px-6 bg-[#FF6B9D] hover:bg-[#FF6B9D]/90 text-[#0F0B1F]">
              Get in Touch
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default InvestorsPage;
